import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight,
  Sparkles, Crown, Heart, Settings, Pencil,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { ArchetypeSheet, ARCHETYPE_LABELS, type Archetype } from "@/components/profile/ArchetypeSheet";
import { MembershipSection } from "@/components/profile/MembershipSection";
import { AccountSettings } from "@/components/profile/AccountSettings";
import { supabase } from "@/lib/supabase";
import { useProfile } from "@/lib/useProfile";
import { AppShell } from "@/components/app-shell";
import { useResolvedPhotoUrl } from "@/components/ui-extras";
import { useSubscription } from "@/lib/useSubscription";
import { CollectionSummary } from "@/components/collection-prompts";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";


export const Route = createFileRoute("/account")({
  head: () => ({
    meta: [
      { title: "Your Cailève — Cailève" },
      { name: "description", content: "Manage your Cailève profile." },
    ],
  }),
  component: () => (
    <AppShell>
      <AccountPage />
    </AppShell>
  ),
});

function AccountPage() {
  const { user } = useAuth();
  if (!user) return null;
  const uid = user.id;
  const qc = useQueryClient();
  const { isFree, tier, isPremium, isAtelier, isFoundingMember } = useSubscription();

  const [nameSheetOpen, setNameSheetOpen] = useState(false);
  const [editPhotoOpen, setEditPhotoOpen] = useState(false);
  const [styleIdentityOpen, setStyleIdentityOpen] = useState(false);
  const [archetypeSheetOpen, setArchetypeSheetOpen] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [membershipOpen, setMembershipOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [archetypeQuizOpen, setArchetypeQuizOpen] = useState(false);

  const { data: profile, isLoading } = useProfile();

  const { data: eyeStats } = useQuery({
    queryKey: ["eye_stats", uid],
    enabled: !!uid,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("ecrin_eye_scans")
        .select("verdict_type, item_saved")
        .eq("user_id", uid);
      const rows = data ?? [];
      return {
        total: rows.length,
        saved: rows.filter((r) => r.item_saved).length,
        fits: rows.filter((r) => r.verdict_type === "fits").length,
        wait: rows.filter((r) => r.verdict_type === "wait").length,
      };
    },
  });

  const { data: styleMemory } = useQuery({
    queryKey: ["style_memory", uid],
    enabled: !!uid,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const [jewFavs, clothingFavs, accessoryFavs, requests, feelingsRes, clothingFeelingsRes] = await Promise.all([
        supabase
          .from("jewelry_items")
          .select("name, is_favorite, favorited_at")
          .eq("user_id", uid)
          .eq("is_favorite", true)
          .eq("is_accessory", false)
          .order("favorited_at", { ascending: false })
          .limit(5),
        supabase
          .from("clothing_items")
          .select("name, is_favorite, favorited_at")
          .eq("user_id", uid)
          .eq("is_favorite", true)
          .order("favorited_at", { ascending: false })
          .limit(5),
        supabase
          .from("jewelry_items")
          .select("name, is_favorite, favorited_at")
          .eq("user_id", uid)
          .eq("is_favorite", true)
          .eq("is_accessory", true)
          .order("favorited_at", { ascending: false })
          .limit(5),
        supabase
          .from("style_requests")
          .select("event, mood, want_to_feel")
          .eq("user_id", uid)
          .order("created_at", { ascending: false })
          .limit(20),
        supabase
          .from("jewelry_items")
          .select("feelings")
          .eq("user_id", uid)
          .eq("is_wishlist", false)
          .not("feelings", "eq", "{}")
          .limit(30),
        supabase
          .from("clothing_items")
          .select("feelings")
          .eq("user_id", uid)
          .eq("is_wishlist", false)
          .not("feelings", "eq", "{}")
          .limit(30),
      ]);

      // Merge all favorites, most recently favorited first, top 3
      const allFavs = [
        ...(jewFavs.data ?? []),
        ...(clothingFavs.data ?? []),
        ...(accessoryFavs.data ?? []),
      ]
        .filter((f) => f.name)
        .sort((a, b) =>
          (b.favorited_at ?? "").localeCompare(a.favorited_at ?? "")
        );
      const topPieces = Array.from(
        new Set(allFavs.map((f) => f.name as string))
      ).slice(0, 3);

      // Aggregate moods across recent style requests
      const moodCount: Record<string, number> = {};
      for (const r of requests.data ?? []) {
        for (const m of [r.mood, r.want_to_feel]) {
          if (m) {
            for (const word of m.split(",").map((s: string) => s.trim()).filter(Boolean)) {
              moodCount[word] = (moodCount[word] ?? 0) + 1;
            }
          }
        }
      }
      const topMoods = Object.entries(moodCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([m]) => m);

      // Aggregate events
      const eventCount: Record<string, number> = {};
      for (const r of requests.data ?? []) {
        if (r.event) eventCount[r.event] = (eventCount[r.event] ?? 0) + 1;
      }
      const topEvents = Object.entries(eventCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([e]) => e);

      const feelingCount: Record<string, number> = {};
      for (const item of [...(feelingsRes.data ?? []), ...(clothingFeelingsRes.data ?? [])]) {
        for (const f of (item.feelings as string[] | null) ?? []) {
          if (f) feelingCount[f] = (feelingCount[f] ?? 0) + 1;
        }
      }
      const topFeelings = Object.entries(feelingCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4)
        .map(([f]) => f);
      return { topPieces, topMoods, topEvents, topFeelings };
    },
  });

  const [name, setName] = useState("");
  const [photoPath, setPhotoPath] = useState<string | null>(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("caileve_dark_mode") === "1";
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("caileve_dark_mode", "1");
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute("content", "#1A1020");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("caileve_dark_mode", "0");
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute("content", "#F8F5FA");
    }
  }, [darkMode]);

  useEffect(() => {
    const saved = localStorage.getItem("caileve_dark_mode") === "1";
    if (saved) document.documentElement.classList.add("dark");
  }, []);

  useEffect(() => {
    if (profile) {
      setName(profile.name ?? "");
      setPhotoPath(profile.photo_url ?? null);
    }
  }, [profile]);

  const { url: previewUrl, refresh: refreshPreview } = useResolvedPhotoUrl(photoPath);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        id: uid,
        name: name.trim() || null,
        photo_url: photoPath,
      };
      const { error } = await supabase
        .from("profiles")
        .upsert(payload as never, { onConflict: "id" });
      if (error) throw new Error(error.message || "Network error");
    },
    onSuccess: () => {
      toast.success("Profile updated");
      qc.invalidateQueries({ queryKey: ["profile", uid] });
    },
    onError: (e: Error) => toast.error(e.message),
  });


  const initial = (name || user?.email || "?").slice(0, 1).toUpperCase();

  const pillLabel = isAtelier
    ? "ATELIER"
    : isFoundingMember
    ? "FOUNDING MEMBER"
    : isPremium
    ? "PREMIUM"
    : "CAILÈVE MEMBER";


  const SERIF = "'Cormorant Garamond', Georgia, serif";

  const cardStyle = (dark: boolean): React.CSSProperties => ({
    borderRadius: "var(--caileve-radius-lg)",
    border: "1px solid rgba(155,127,168,0.22)",
    background: dark
      ? "linear-gradient(145deg, rgba(45,31,58,0.82), rgba(26,16,32,0.92))"
      : "linear-gradient(145deg, rgba(255,255,255,0.62), rgba(237,228,240,0.74))",
    boxShadow: dark
      ? "0 12px 30px rgba(0,0,0,0.28)"
      : "0 12px 30px rgba(30,22,36,0.06)",
    padding: "18px 20px",
  });

  const iconCircleStyle = (dark: boolean): React.CSSProperties => ({
    width: 56,
    height: 56,
    borderRadius: "50%",
    border: "1px solid rgba(155,127,168,0.24)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--caileve-gold-text)",
    background: dark ? "rgba(45,31,58,0.60)" : "rgba(248,245,250,0.68)",
    flexShrink: 0,
  });

  const sheetRowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 0",
    borderBottom: "0.5px solid var(--caileve-soft)",
  };

  const sheetLabelStyle: React.CSSProperties = {
    fontFamily: "Inter, sans-serif",
    fontSize: 10,
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    color: "var(--caileve-muted)",
    marginBottom: 3,
  };

  const sheetValueStyle: React.CSSProperties = {
    fontFamily: "Inter, sans-serif",
    fontSize: 15,
    color: "var(--caileve-ink)",
  };


  const profileCards = [
    {
      id: "archetype",
      icon: <Sparkles style={{ width: 22, height: 22 }} strokeWidth={1.5} />,
      title: profile?.style_archetype_primary
        ? ARCHETYPE_LABELS[profile.style_archetype_primary as Archetype] ?? profile.style_archetype_primary
        : "Your Style Profile",
      subtitle: profile?.style_archetype_primary
        ? profile.style_archetype_secondary
          ? `with a current of ${ARCHETYPE_LABELS[profile.style_archetype_secondary as Archetype] ?? profile.style_archetype_secondary}`
          : "Cailève knows how you dress."
        : "Discover your style archetype.",
      onClick: () => {
        if (profile?.style_archetype_primary) {
          setArchetypeSheetOpen(true);
        } else {
          setArchetypeQuizOpen(true);
        }
      },
    },
    {
      id: "preferences",
      icon: <Heart style={{ width: 22, height: 22 }} strokeWidth={1.5} />,
      title: "Preferences",
      subtitle: "Your style, occasions & display preferences",
      onClick: () => setPreferencesOpen(true),
    },
    {
      id: "membership",
      icon: <Crown style={{ width: 22, height: 22 }} strokeWidth={1.5} />,
      title: "Membership",
      subtitle: isAtelier
        ? "Atelier · Active"
        : isFoundingMember && isPremium
        ? "Founding Member · $6.99 locked"
        : isFoundingMember && isAtelier
        ? "Atelier Founding Member · $14.99 locked"
        : isPremium
        ? "Cailève Member · Active"
        : "Free plan",
      onClick: () => setMembershipOpen(true),
    },
    {
      id: "settings",
      icon: <Settings style={{ width: 22, height: 22 }} strokeWidth={1.5} />,
      title: "App Settings",
      subtitle: "Notifications, privacy, and more",
      onClick: () => setSettingsOpen(true),
    },
  ];


  return (
    <div className="max-w-lg mx-auto px-6 pt-10 pb-36">
      {isLoading ? (
        <ProfileSkeleton dark={darkMode} />
      ) : (
        <>
          {/* ── SETTINGS SHORTCUT ── */}
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <button
              type="button"
              aria-label="App settings"
              onClick={() => setSettingsOpen(true)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--caileve-gold-text)",
                padding: 4,
              }}
            >
              <Settings style={{ width: 20, height: 20 }} strokeWidth={1.5} />
            </button>
          </div>

          {/* ── IDENTITY BLOCK ── */}
          <div className="flex flex-col items-center text-center" style={{ marginBottom: 24 }}>
            <div style={{ position: "relative" }}>
              <div
                style={{
                  width: 96,
                  height: 96,
                  borderRadius: "50%",
                  border: "1.5px solid var(--caileve-gold)",
                  padding: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    overflow: "hidden",
                    backgroundColor: "var(--caileve-soft)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt={name || "Profile"}
                      className="w-full h-full object-cover"
                      onError={() => refreshPreview()}
                    />
                  ) : (
                    <span style={{ fontFamily: SERIF, fontSize: 32, color: "var(--caileve-lilac)" }}>
                      {initial}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                aria-label="Edit photo"
                onClick={() => setEditPhotoOpen(true)}
                style={{
                  position: "absolute",
                  bottom: 4,
                  right: 0,
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  background: "var(--caileve-bg)",
                  border: "1px solid var(--caileve-soft)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "var(--caileve-lilac)",
                }}
              >
                <Pencil style={{ width: 14, height: 14 }} strokeWidth={1.5} />
              </button>
            </div>

            <button
              type="button"
              onClick={() => setNameSheetOpen(true)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                textAlign: "center",
                display: "block",
                marginTop: 14,
              }}
            >
              <p style={{ fontFamily: SERIF, fontSize: 24, color: "var(--caileve-ink)", fontWeight: 400 }}>
                {name || "Add your name"}
              </p>
            </button>

            <div style={{ marginTop: 8 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontFamily: "Inter, sans-serif",
                  fontSize: 10,
                  letterSpacing: "0.25em",
                  textTransform: "uppercase",
                  color: "var(--caileve-gold-text)",
                  border: "1px solid var(--caileve-gold)",
                  borderRadius: "var(--caileve-radius-pill)",
                  padding: "4px 12px",
                }}
              >
                <Sparkles style={{ width: 11, height: 11 }} strokeWidth={1.5} />
                {pillLabel}
              </span>
            </div>

            <p
              style={{
                fontFamily: SERIF,
                fontStyle: "italic",
                fontSize: 13,
                color: "var(--caileve-muted)",
                opacity: 0.7,
                marginTop: 12,
                lineHeight: 1.5,
              }}
            >
              Refined taste. Effortless style. That&apos;s your signature.
            </p>
          </div>

          <div style={{ height: 1, backgroundColor: "rgba(198,166,107,0.28)", marginTop: 20, marginBottom: 20 }} />
          {/* ── PROFILE CARDS ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {profileCards.map((card) => (
              <button
                key={card.id}
                type="button"
                onClick={card.onClick}
                style={{ ...cardStyle(darkMode), textAlign: "left", cursor: "pointer", width: "100%" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={iconCircleStyle(darkMode)}>{card.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontFamily: SERIF, fontSize: 19, color: "var(--caileve-ink)", fontWeight: 500 }}>
                      {card.title}
                    </p>
                    <p
                      style={{
                        fontFamily: "Inter, sans-serif",
                        fontSize: 13,
                        color: "var(--caileve-muted)",
                        marginTop: 2,
                      }}
                    >
                      {card.subtitle}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0" style={{ color: "var(--caileve-muted)" }} />
                </div>
              </button>
            ))}
          </div>

          <ArchetypeSheet
            uid={uid}
            profile={profile}
            topFeelings={styleMemory?.topFeelings}
            styleMemory={styleMemory}
            darkMode={darkMode}
            archetypeSheetOpen={archetypeSheetOpen}
            setArchetypeSheetOpen={setArchetypeSheetOpen}
            archetypeQuizOpen={archetypeQuizOpen}
            setArchetypeQuizOpen={setArchetypeQuizOpen}
          />
        </>
      )}

      {/* ══════════════ SHEET 2 — PREFERENCES ══════════════ */}
      <Sheet open={preferencesOpen} onOpenChange={setPreferencesOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl border-0 pb-10" style={{ backgroundColor: "var(--caileve-bg)" }}>
          <div className="mx-auto mt-2 mb-4 h-1 w-10 rounded-full bg-[var(--caileve-muted)]/40" />
          <SheetHeader>
            <SheetTitle className="font-serif-display text-2xl text-center" style={{ color: "var(--caileve-ink)" }}>
              Preferences
            </SheetTitle>
          </SheetHeader>

          <div className="mt-6 px-2">
            <button
              type="button"
              onClick={() => { setPreferencesOpen(false); setNameSheetOpen(true); }}
              style={{ ...sheetRowStyle, width: "100%", background: "none", textAlign: "left" }}
            >
              <div>
                <p style={sheetLabelStyle}>Name</p>
                <p style={sheetValueStyle}>{name || "Add your name"}</p>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0" style={{ color: "var(--caileve-muted)" }} />
            </button>

            <button
              type="button"
              onClick={() => { setPreferencesOpen(false); setEditPhotoOpen(true); }}
              style={{ ...sheetRowStyle, width: "100%", background: "none", textAlign: "left" }}
            >
              <div>
                <p style={sheetLabelStyle}>Profile Photo</p>
                <p style={sheetValueStyle}>{photoPath ? "Photo saved" : "Add a photo"}</p>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0" style={{ color: "var(--caileve-muted)" }} />
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ══════════════ SHEET 3 — MEMBERSHIP ══════════════ */}
      <MembershipSection
        open={membershipOpen}
        onOpenChange={setMembershipOpen}
        profile={profile}
        darkMode={darkMode}
      />


      {/* ══════════════ SHEET 4 — APP SETTINGS ══════════════ */}
      <AccountSettings
        settingsOpen={settingsOpen}
        onSettingsOpenChange={setSettingsOpen}
        editPhotoOpen={editPhotoOpen}
        onEditPhotoOpenChange={setEditPhotoOpen}
        nameSheetOpen={nameSheetOpen}
        onNameSheetOpenChange={setNameSheetOpen}
        uid={uid}
        name={name}
        onNameChange={setName}
        photoPath={photoPath}
        onPhotoPathChange={setPhotoPath}
        darkMode={darkMode}
        onDarkModeChange={setDarkMode}
        save={save}
      />
    </div>
  );
}

function ProfileSkeleton({ dark }: { dark: boolean }) {
  const pulse: React.CSSProperties = {
    borderRadius: 8,
    backgroundColor: "var(--caileve-soft)",
    animation: "pulse 2s cubic-bezier(0.4,0,0.6,1) infinite",
    opacity: dark ? 0.4 : 1,
  };
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-3">
        <div style={{ ...pulse, width: 96, height: 96, borderRadius: "50%" }} />
        <div style={{ ...pulse, width: 140, height: 22 }} />
        <div style={{ ...pulse, width: 100, height: 14 }} />
      </div>
      {[1, 2, 3, 4].map((i) => (
        <div key={i} style={{ ...pulse, height: 86, borderRadius: "var(--caileve-radius-lg)" }} />
      ))}
    </div>
  );
}
